import React from 'react';
import {
  Tooltip as ChakraTooltip,
  TooltipProps as ChakraTooltipProps,
  Text,
  Box,
  forwardRef,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { getToolTipTitleStyles } from './tooltip.theme';

export type ToolTipPlacement = 'bottom' | 'top' | 'left' | 'right';

export interface TooltipProps extends ChakraTooltipProps {
  /**
   * The title of the tooltip.
   */
  title?: string;
  /**
   * The label of the tooltip.
   */
  label?: React.ReactNode;
  /**
   * The color mode that the tooltip will display.
   * @default light
   */
  colorMode?: ColorMode;
  /**
   * The placement of the popper relative to its reference.
   * @default bottom
   */
  placement?: ToolTipPlacement;
  /**
   * The accessible, human friendly label to use for screen readers. If passed, tooltip will show the content label but expose only `aria-label` to assistive technologies.
   */
  'aria-label'?: string;
}

/**
 * A tooltip is a brief, informative message that appears when a user interacts with an element.
 */
export const Tooltip = forwardRef<TooltipProps, 'div'>(
  (
    {
      title,
      label,
      colorMode: propColorMode,
      placement = 'bottom',
      'aria-label': ariaLabel,
      children,
      ...props
    },
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    /* 
        The colorScheme value is populated by the colorMode prop. Had to do it
        this way because we cannot extend the chakra-ui component props and
        we kept getting an error for using custom props on a chakra component.
        Keep in mind that not all of our themed library components require
        this method. We also could not use the colorScheme property for our color
        mode changes because certain components, like button, was already using
        the colorScheme property.
    */
    const colorScheme = colorMode;

    const getTranslateDirection = () => {
      switch (placement) {
        case 'top':
          return 'translate(0px, 8px)';
        case 'bottom':
          return 'translate(0px, -8px)';
        case 'left':
          return 'translate(8px, 0px)';
        case 'right':
          return 'translate(-8px, 0px)';
        default:
          return 'translate(0px, -8px)';
      }
    };

    return (
      <ChakraTooltip
        ref={ref}
        motionProps={{
          initial: {
            opacity: 0,
            transform: getTranslateDirection(),
          },
          animate: { opacity: 1, transform: 'translate(0px, 0px)' },
          exit: {
            opacity: 0,
            transform: getTranslateDirection(),
            transition: {
              ease: 'easeOut',
            },
          },
          transition: {
            duration: 0.3,
          },
        }}
        colorScheme={colorScheme}
        aria-label={ariaLabel}
        placement={placement}
        label={
          title ? (
            <Box>
              <Text aria-label={'tooltip-title'} sx={getToolTipTitleStyles()}>
                {title}
              </Text>
              {label}
            </Box>
          ) : (
            label
          )
        }
        {...props}
      >
        {children}
      </ChakraTooltip>
    );
  }
);
